import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import '@/App.css';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import StudentDashboard from './pages/StudentDashboard';
import StudentQuiz from './pages/StudentQuiz';
import StudentLive from './pages/StudentLive';
import StudentProfile from './pages/StudentProfile';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CourseCatalog from './pages/CourseCatalog';
import CourseView from './pages/CourseView';
import LiveClass from './pages/LiveClass';
import TestInterface from './pages/TestInterface';
import CreateCourse from './pages/CreateCourse';
import CreateLiveClass from './pages/CreateLiveClass';
import CreateQuiz from './pages/CreateQuiz';
import ManageResources from './pages/ManageResources';
import ManageEnrollments from './pages/ManageEnrollments';
import StudentResources from './pages/StudentResources';
import PaymentSuccess from './pages/PaymentSuccess';
import ResourcePaymentSuccess from './pages/ResourcePaymentSuccess';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/courses" element={<CourseCatalog />} />
            
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/payment/resource/success"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <ResourcePaymentSuccess />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/dashboard"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/quiz"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentQuiz />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/live"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentLive />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/profile"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentProfile />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/dashboard"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/create-course"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <CreateCourse />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/create-live-class"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <CreateLiveClass />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/create-quiz"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <CreateQuiz />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/manage-resources"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ManageResources />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/teacher/manage-enrollments"
              element={
                <ProtectedRoute allowedRoles={['teacher']}>
                  <ManageEnrollments />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/student/resources"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentResources />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/course/:courseId"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <CourseView />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/live-class/:classId"
              element={
                <ProtectedRoute>
                  <LiveClass />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/test/:testId"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <TestInterface />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
